import { useCustomerNetAcquisitionLimits } from '@src/hooks/useCustomer';
import {
  Dialog,
  LoaderTalos,
  LoaderWrapper,
  NotificationVariants,
  useCurrenciesContext,
  useGlobalToasts,
  type CustomerNetAcquisitionLimitConfig,
  type DialogProps,
} from '@talos/kyoko';
import { isEmpty } from 'lodash-es';
import { useEffect, useState } from 'react';
import { useAcquisitionLimitConfigMutation, useAcquisitionLimitConfigQuery } from '../queries';
import { DialogContents } from './DialogContents';

export function ConfigureLimitsDialog({ ...props }: DialogProps) {
  const { add: addToast } = useGlobalToasts();

  const { isLoading: isConfigLoading, data: initialConfig } = useAcquisitionLimitConfigQuery();

  const [config, setConfig] = useState<CustomerNetAcquisitionLimitConfig>({});
  useEffect(() => {
    if (initialConfig) {
      setConfig(initialConfig);
    }
  }, [setConfig, initialConfig]);

  const { mutateAsync: mutateConfig } = useAcquisitionLimitConfigMutation();
  const handleModifyConfig = () => {
    mutateConfig(config)
      .then(() => {
        addToast({
          text: `Updated Acquisition Limit Configuration`,
          variant: NotificationVariants.Positive,
        });
      })
      .catch(() => {
        addToast({
          text: `Failed to update Acquisition Limit Configuration`,
          variant: NotificationVariants.Negative,
        });
      });
  };

  const { currenciesBySymbol } = useCurrenciesContext();
  const acquisitionLimits = useCustomerNetAcquisitionLimits({ tag: 'ConfigureLimitsDialog' });

  const isLoading = isConfigLoading || acquisitionLimits == null;
  const canSaveConfig =
    !isLoading &&
    (!isEmpty(config.SpecifiedAssets) || config.IncludeAllFiatAsSpecified || config.IncludeAllStableCoinAsSpecified);

  return (
    <Dialog
      {...props}
      confirmLabel="Save"
      title="Net Acquisition Global Configuration"
      showClose={true}
      width={650}
      autoFocusFirstElement={false}
      confirmDisabled={!canSaveConfig}
      onConfirm={handleModifyConfig}
    >
      {isLoading ? (
        <LoaderWrapper>
          <LoaderTalos />
        </LoaderWrapper>
      ) : (
        <DialogContents
          currenciesBySymbol={currenciesBySymbol}
          acquisitionLimits={acquisitionLimits}
          config={config}
          onConfigChange={setConfig}
        />
      )}
    </Dialog>
  );
}
